import Dexie, { type Table } from 'dexie';
import type { Client, ComplianceDocument, EntityMap, Job, JobRecord, LatLng, Namespace, PestActivity, RodentPlan, SyncMeta, Technician } from './types';

export type Stored<T> = T & SyncMeta;

export type OutboxStatus = 'pending' | 'conflict' | 'rejected';

interface OutboxBase {
  seq?: number;
  opId: string;
  /** Account that made the change. Only that account's session may send it. */
  ownerId: string;
  /** Identifies the thing being changed, e.g. "jobs/abc". Used to re-base and to protect unsynced local rows. */
  entityKey: string;
  label: string;
  status: OutboxStatus;
  attempts: number;
  lastError?: string;
  createdAt: string;
}

export interface RecordOp extends OutboxBase {
  kind: 'record';
  type: 'put' | 'delete';
  namespace: Namespace;
  key: string;
  value?: unknown;
  baseRevision: number | null;
  /** Server copy returned with a conflict, shown in the review dialog. */
  server?: { value: unknown; revision: number; deleted?: boolean; updatedAt?: string } | null;
}

export interface FileOp extends OutboxBase {
  kind: 'file';
  fileId: string;
  meta: { namespace: string; key: string; name: string; type: string };
}

export interface DocumentOp extends OutboxBase {
  kind: 'document';
  action: 'upload' | 'update' | 'delete';
  documentId: string;
  meta?: { name: string; documentName: string; tag: string; expiry: string; issuedDate: string; reviewDateAutoFilled?: boolean; type: string };
  /** Blob id in the blobs table when action is "upload". */
  blobId?: string;
}

/** Requests queued by the pre-React prototype, replayed verbatim. */
export interface LegacyRequestOp extends OutboxBase {
  kind: 'legacy-request';
  url: string;
  method: string;
  headers: Record<string, string>;
  blobId: string;
}

export type OutboxOp = RecordOp | FileOp | DocumentOp | LegacyRequestOp;

export interface StoredBlob {
  id: string;
  blob: Blob;
  name: string;
  type: string;
  size: number;
  createdAt: string;
  /** True once the server has a copy; false while it is only on this device. */
  uploaded: boolean;
}

export interface MetaRow { key: string; value: unknown }
export interface GeocodeRow { address: string; location: LatLng | null; at: number }

export class PcsDatabase extends Dexie {
  clients!: Table<Stored<Client>, string>;
  jobs!: Table<Stored<Job>, string>;
  technicians!: Table<Stored<Technician>, string>;
  jobRecords!: Table<Stored<JobRecord>, string>;
  pestActivity!: Table<Stored<PestActivity>, string>;
  rodentPlans!: Table<Stored<RodentPlan>, string>;
  documents!: Table<ComplianceDocument, string>;
  outbox!: Table<OutboxOp, number>;
  blobs!: Table<StoredBlob, string>;
  meta!: Table<MetaRow, string>;
  geocodes!: Table<GeocodeRow, string>;

  constructor(name = 'pcs-internal') {
    super(name);
    this.version(1).stores({
      clients: 'id, name',
      jobs: 'id, date, techName, clientId, clientName',
      technicians: 'id, name',
      jobRecords: 'id, jobId, clientId, clientName, type',
      pestActivity: 'id, clientName, status',
      rodentPlans: 'id, clientId, clientName',
      documents: 'id, updatedAt',
      // ++seq is the outbox order: strictly increasing, so the queue drains first-in, first-out.
      outbox: '++seq, ownerId, entityKey, status',
      blobs: 'id',
      meta: 'key',
      geocodes: 'address'
    });
  }
}

export const db = new PcsDatabase();

const tables: { [N in Namespace]: (database: PcsDatabase) => Table<Stored<EntityMap[N]>, string> } = {
  clients: (database) => database.clients,
  jobs: (database) => database.jobs,
  technicians: (database) => database.technicians,
  'job-records': (database) => database.jobRecords,
  'pest-activity': (database) => database.pestActivity,
  'rodent-plans': (database) => database.rodentPlans
};

export const NAMESPACES = Object.keys(tables) as Namespace[];
export const isNamespace = (value: string): value is Namespace => value in tables;
export function tableFor<N extends Namespace>(namespace: N, database: PcsDatabase = db): Table<Stored<EntityMap[N]>, string> {
  return tables[namespace](database);
}
export const entityKey = (namespace: string, key: string) => `${namespace}/${key}`;

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row ? (row.value as T) : fallback;
}
export const setMeta = (key: string, value: unknown) => db.meta.put({ key, value });
